import { ChangeDetectionStrategy, Component, computed, EventEmitter, HostListener, inject, Input, OnInit, Output, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Constants } from '../../../common/helper/Constants';
import { TranslatePipe } from '@ngx-translate/core';
import { UserService } from '../../../common/service/user.service';

import { environment } from '../../../environment/environment';
import { MenuComponent } from '../header/menu/menu.component';
import { PanelModule } from 'primeng/panel';
import { Role } from '../../auth/role.enum';
import { PermissionDirective } from '../../auth/permission.directive';
import { ComponentBase } from '../../../common/component-base';
import { CurrencyPipe, NgClass } from '@angular/common';
import { TranslationService } from '../../../common/service/translate.service';
import { AuthService } from '../../auth/auth.service';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { ShoppingCartMenuComponent } from '../header/menu/shopping-cart-menu/shopping-cart-menu.component';
import { PlatformService } from '../../../common/service/platform.service';
import { UserPreferenceService } from '../../../common/service/user-preference.service';
import { CustomerMenuItem } from '../../../common/model/customer/customer-menu-item';
import { CustomerService } from '../../../common/service/customer.service';
import { Customer } from '../../../common/model/customer/customer';
import { clearAllTableSessionStates } from '../../../common/helper/table-session-state';
import { CartService } from '../../../feature/shop/cart.service';

@Component({
  selector: 'gate-navigation',
  standalone: true,
  imports: [PanelModule, RouterLink, RouterLinkActive, TranslatePipe, PermissionDirective, NgClass, CurrencyPipe, ToggleSwitchModule, FormsModule, MenuComponent, ShoppingCartMenuComponent],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationComponent extends ComponentBase implements OnInit {
  private readonly userService = inject(UserService);
  private readonly cartService = inject(CartService);
  private readonly router = inject(Router);
  private readonly translationService = inject(TranslationService);
  private readonly userPreferenceService = inject(UserPreferenceService);
  private readonly authService = inject(AuthService);
  private readonly customerService = inject(CustomerService);
  public routes = Constants.routes;
  public serviceCaseUrl: string = environment.ServiceCaseUrl;
  public role = Role;
  public user = computed(() => this.userService.currentUser());
  public cartItemCount = signal<number>(0);
  public cartTotalPrice = signal<number>(0);
  private customerId = signal<string | null>(null);
  @Output() onClickMenu: EventEmitter<any> = new EventEmitter<any>();
  @Output() customerSelected = new EventEmitter<string>();
  @Output() preferredCustomerSelected = new EventEmitter<{ customerId: string; event: Event }>();
  @Input() public currentCustomerName = '';
  @Input() public currentCustomerId = '';
  @Input() public customer: Customer | undefined;
  @Input() public isCustomerLoading = false;
  @Input() public customersMenuItem: CustomerMenuItem[] = [];
  @Input() public preferredCustomerLoadingId: string | null = null;
  public isCustomer = computed(() => this.userService.isCustomerContext());
  public isCompanyImpersonatedByAcpUser = computed(() => this.userService.isCompanyImpersonatedByAcpUser());
  public isLightMode = signal(true);
  public isHeroVisible = signal(true);
  public desktopSidebarOpen = signal(true);
  public isAnyMobileFullViewOpen = computed(() =>
    this.isSectionOpen('customers') || this.isSectionOpen('settings') || this.isSectionOpen('cart')
  );
  public showMobileCustomerTrigger = computed(() =>
    this.isMobileView() && this.customersMenuItem.length > 0 && !this.isAnyMobileFullViewOpen()
  );
  public mobileSectionState = signal<Record<string, boolean>>({
    customers: false,
    settings: false,
    cart: false,
    products: false,
    shop: false,
    documents: false,
    service: false,
    reports: false,
    userManagement: false,
    customerAdministration: false,
  });
  private threatTickerUrl: string = "https://www.acp-gruppe.com/threat-ticker";
  private ESSPUrl: string = "https://essp.acp-it.eu/";
  private apiDocumentationUrl: string = `${environment.partnerApiDocumentationUrl}`;
  isMobileView = signal<boolean>(window.innerWidth < 1024);

  @HostListener('window:resize')
  onResize() {
    this.isMobileView.set(window.innerWidth < 1024);
  }
  constructor() {
    super();
    this.platformService = inject(PlatformService);
  }

  ngOnInit(): void {
    this.openSectionForCurrentRoute();
    this.applyPreferenceState();
    this.userPreferenceService.initialize().then(() => {
      this.applyPreferenceState();
      this.platformService.heroToggleEvent.emit(this.isHeroVisible());
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.openSectionForCurrentRoute();
      }
    });

    this.platformService.heroToggleEvent.subscribe((value) => {
      this.isHeroVisible.set(value);
    });

    this.desktopSidebarOpen.set(this.platformService.getIsDesktopSidebarOpen());
    this.platformService.desktopSidebarToggleEvent.subscribe((value) => {
      this.desktopSidebarOpen.set(value);
    });

    this.userService.waitForImpersonationToComplete().then(() => {
      this.loadCartInfo();
    });
    this.cartService.shoppingItemCountUpdate.subscribe(() => {
      this.loadCartInfo();
    });
    this.userService.impersonationStatus.subscribe(() => {
      this.loadCartInfo();
    });
    this.loadCartInfo();
  }

  public loadCartInfo(): void {
    const customerId = this.userService.getCustomerNumber();
    this.customerId.set(customerId);

    const groupRoles = this.userService.getRoles('group');
    const hasShopPermission = groupRoles.some((role) =>
      [Role.GateEcommerceAdmin, Role.GateEcommerceOrderApprover, Role.GateEcommerceOrderRequester].includes(role)
    );

    if (!hasShopPermission) {
      this.cartItemCount.set(0);
      this.cartTotalPrice.set(0);
      return;
    }

    this.cartService
      .getCart(customerId)
      .then((cart) => {
        this.cartItemCount.set(cart.totalQuantity ?? 0);
        this.cartTotalPrice.set(cart.totalPrice ?? 0);
      })
      .catch(() => {
        this.cartItemCount.set(0);
        this.cartTotalPrice.set(0);
      });
  }

  onClickMenuEvent() {
    this.onClickMenu.emit();
  }

  toggleDesktopSidebar() {
    this.platformService.setDesktopSidebarOpen(!this.desktopSidebarOpen());
  }

  toggleSection(section: string) {
    this.mobileSectionState.update((state) => {
      const nextIsOpen = !state[section];
      const resetState = Object.keys(state).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);

      return {
        ...resetState,
        [section]: nextIsOpen,
      };
    });
  }

  isSectionOpen(section: string): boolean {
    return !!this.mobileSectionState()[section];
  }

  openCustomerView() {
    this.mobileSectionState.update((state) => {
      const resetState = Object.keys(state).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);

      return {
        ...resetState,
        customers: true,
      };
    });
  }

  openSettingsView() {
    this.mobileSectionState.update((state) => {
      const resetState = Object.keys(state).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);

      return {
        ...resetState,
        settings: true,
      };
    });
  }

  openCartView() {
    this.mobileSectionState.update((state) => {
      const resetState = Object.keys(state).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);

      return {
        ...resetState,
        cart: true,
      };
    });
  }

  closeSettingsView() {
    this.mobileSectionState.update((state) => ({
      ...state,
      settings: false,
    }));
    this.openSectionForCurrentRoute();
  }

  closeCartView() {
    this.mobileSectionState.update((state) => ({
      ...state,
      cart: false,
    }));
    this.openSectionForCurrentRoute();
  }

  navigateToCart() {
    if (this.isMobileView()) {
      this.closeCartView();
    }

    this.onClickMenuEvent();
  }

  closeCustomerView() {
    this.mobileSectionState.update((state) => ({
      ...state,
      customers: false,
    }));
    this.openSectionForCurrentRoute();
  }

  closeMobileMenu() {
    this.mobileSectionState.update((state) => {
      const resetState = Object.keys(state).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);

      return resetState;
    });
    this.onClickMenuEvent();
  }

  switchCustomer(customerId: string) {
    if (this.isMobileView()) {
      this.closeCustomerView();
    }

    if (this.currentCustomerId === customerId) {
      this.onClickMenuEvent();
      return;
    }

    this.onClickMenuEvent();
    this.customerSelected.emit(customerId);
  }

  setPreferredCustomer(customerId: string, event: Event) {
    event.stopPropagation();
    if (this.preferredCustomerLoadingId || this.userService.getPreferredCustomerNumber() === customerId) {
      return;
    }

    this.preferredCustomerSelected.emit({ customerId, event });
  }

  navigateThreatTicker() {
    this.onClickMenuEvent();
    this.navigateToNewTab(this.threatTickerUrl);
  }
  navigateESSP() {
    this.onClickMenuEvent();
    this.navigateToNewTab(this.ESSPUrl);
  }

  navigateApiDocumentation() {
    this.onClickMenuEvent();
    this.navigateToNewTab(this.apiDocumentationUrl);
  }

  toggleLanguage() {
    const newLang = this.locale() === 'de-DE' ? 'en-US' : 'de-DE';
    this.translationService.setLanguage(newLang);
    this.userPreferenceService.setLanguage(newLang);
  }

  toggleDarkMode() {
    const isDarkMode = !this.userPreferenceService.isDarkMode();
    this.isLightMode.set(!isDarkMode);
    this.userPreferenceService.setDarkMode(isDarkMode);
  }

  toggleHeroVisibility(nextValue: boolean) {
    this.isHeroVisible.set(nextValue);
    this.platformService.heroToggleEvent.emit(nextValue);
    this.userPreferenceService.setShowHeroSection(nextValue);
  }

  navigateToProfile() {
    window.open(environment.UserProfileUpdateUrl, '_blank');
  }

  navigateToMasterData() {
    this.onClickMenuEvent();
    this.router.navigate([this.routes.customerMasterData()]);
  }

  navigateToFeedback() {
    this.onClickMenuEvent();
    this.router.navigate([this.routes.feedback()]);
  }

  logOut() {
    this.customerService.clearCustomerCache();
    clearAllTableSessionStates();
    this.authService.logout();
  }

  public customerLogoUrl(): string | null {
    return this.customerService.resolveCustomerLogoUrl(this.customer?.logoPath);
  }

  private openSectionForCurrentRoute() {
    const url = this.router.url;
    const section = this.getSectionForRoute(url);

    if (!section) {
      return;
    }

    this.mobileSectionState.update((state) => {
      const resetState = Object.keys(state).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);

      return {
        ...resetState,
        [section]: true,
      };
    });
  }

  private getSectionForRoute(url: string): string | null {
    if (
      url.includes(`/${this.routes.licensePath}`) ||
      url.includes(`/${this.routes.marketplacePath}`)
    ) {
      return 'products';
    }

    if (
      url.includes(`/${this.routes.catalogue()}`) ||
      url.includes(`/${this.routes.approval()}`)
    ) {
      return 'shop';
    }

    if (
      url.includes(`/${this.routes.offerPath}`) ||
      url.includes(`/${this.routes.orderPath}`) ||
      url.includes(`/${this.routes.deliveryNotePath}`) ||
      url.includes(`/${this.routes.invoicePath}`) ||
      url.includes(`/${this.routes.contractPath}`) ||
      url.includes(`/${this.routes.externalContractPath}`)
    ) {
      return 'documents';
    }

    if (
      url.includes(`/${this.routes.serviceCase()}`) ||
      url.includes(`/${this.routes.creditPath}`)
    ) {
      return 'service';
    }

    if (
      url.includes(`/${this.routes.inventoryPath}`) ||
      url.includes(`/${this.routes.serviceReportPath}`)
    ) {
      return 'reports';
    }

    if (url.includes(`/${this.routes.customersUser()}`)) {
      return 'userManagement';
    }

    if (
      url.includes(`/${this.routes.customersPath}`) ||
      url.includes(`/${this.routes.userAdministrationPath}`) ||
      url.includes(`/${this.routes.launchpadPath}`) ||
      url.includes(`/${this.routes.modulePath}`)
    ) {
      return 'customerAdministration';
    }

    return null;
  }

  private applyPreferenceState(): void {
    this.isLightMode.set(!this.userPreferenceService.isDarkMode());
    this.isHeroVisible.set(this.userPreferenceService.showHeroSection());
  }
}
